/**
 * Palettes with a 50–900 scale on `:root` (see `src/assets/styles/_variables.scss`).
 */
export const THEME_COLOR_PALETTES = [
  'base',
  'blue',
  'green',
  'orange',
  'neutral',
  'gray',
  'success',
  'warning',
  'error',
] as const;

export type ThemeColorPalette = (typeof THEME_COLOR_PALETTES)[number];

const SHADE_LEVELS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'] as const;

const PALETTE_SET = new Set<string>(THEME_COLOR_PALETTES);

const PALETTE_SHADE_PATTERN = new RegExp(`^(${THEME_COLOR_PALETTES.join('|')})-(${SHADE_LEVELS.join('|')})$`);

/**
 * Resolves a shorthand or token into a `var(--…)` value using `:root` tokens from `_variables.scss`
 * (e.g. `--base-500`), not Tailwind `@theme` `--color-*` aliases.
 *
 * 1. Values already wrapped in `var(...)` are returned unchanged.
 * 2. Custom properties starting with `--` are wrapped: `--base-400` → `var(--base-400)`.
 * 3. Known `palette-shade` (e.g. `base-400`) → `var(--palette-shade)`.
 * 4. Known bare palette (e.g. `base`) → `var(--palette-500)`.
 * 5. Anything else (e.g. `red`, hex, rgb) is returned unchanged.
 */
export const toSystemColor = (color: string): string => {
  const trimmed = color.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (trimmed.startsWith('var(')) {
    return trimmed;
  }

  if (trimmed.startsWith('--')) {
    return `var(${trimmed})`;
  }

  const shadeMatch = PALETTE_SHADE_PATTERN.exec(trimmed);
  if (shadeMatch) {
    const [, palette, shade] = shadeMatch;
    return `var(--${palette}-${shade})`;
  }

  if (PALETTE_SET.has(trimmed)) {
    return `var(--${trimmed}-500)`;
  }

  return trimmed;
};
